// Wave C — pure stitching/sort/filter module for the redesigned online-library
// song rows: one row per song, audio versions and a words publication glued
// together, joined by SONG ID (Wave D — see buildSongRows below for why name
// matching was replaced). No DOM, no window, no localStorage, no i18n import
// — locale is always an explicit parameter, exactly like setlist-sort.ts.

import type { TeamLibrary, LibraryItem, SongTextSummary } from '../library.js';

export interface SongRow {
  /** Ключ строки. `songId` аудио, если он есть, иначе `text:<id>`. */
  key: string;
  songName: string;
  songKey: string | null;
  /** Версии аудио, новые первыми. Пусто — у песни нет записей. */
  versions: LibraryItem[];
  /** Публикация слов или null — у песни нет слов. */
  words: SongTextSummary | null;
  /** Когда слова открывали в последний раз; null — ни разу или слов нет. */
  lastOpenedAt: string | null;
}

export type SongSort = 'recent' | 'alpha';

/**
 * One group per distinct `songId` (or `__share:<shareId>` for legacy shares
 * published before songId existed — they stay separate on purpose). Moved
 * verbatim out of library-view.ts (Wave C) so the rule "what counts as one
 * song" for AUDIO lives in exactly one place; library-view.ts now imports
 * this instead of keeping its own copy.
 */
export interface SongGroup { songId: string; songName: string | null; versions: LibraryItem[]; }

export function groupBySong(items: LibraryItem[]): SongGroup[] {
  const order: string[] = [];
  const map = new Map<string, SongGroup>();
  // items already newest-first from the worker; keep that order within a group.
  for (const it of items) {
    const key = it.songId ?? `__share:${it.shareId}`; // legacy shares without songId stay separate
    let g = map.get(key);
    if (!g) { g = { songId: key, songName: it.songName, versions: [] }; map.set(key, g); order.push(key); }
    g.versions.push(it);
  }
  return order.map(k => {
    const g = map.get(k)!;
    return { ...g, versions: newestFirst(g.versions) };
  });
}

/**
 * Голова списка версий — всегда новейшая запись (единый сетлист, фаза 4:
 * владелец больше не помечает «главную» запись отдельно — `isSongDefault`
 * (`shares.is_song_default`) читался вебом ТОЛЬКО здесь; продукт решил, что
 * голову списка возглавляет новейшая запись, а не выбор владельца, который
 * мог устареть и разойтись с тем, что реально стоит открывать СЕЙЧАС —
 * решение владельца version-choice.ts (личный выбор на устройстве) сильнее
 * старой общей метки. Маршрут `PATCH /api/shares/:id/default` и колонка в
 * базе остаются нетронутыми до фазы 5 (`TD-260924-song-default-route-until-
 * phase-5`, vault `TECH_DEBT.md`) — просто больше никем не читаются здесь.
 *
 * Сортировка ЯВНАЯ, а не полагание на «воркер уже прислал новыми вперёд»:
 * та гарантия — ВНУТРИ одной команды (см. groupBySong), а версии одной песни
 * из РАЗНЫХ команд (`mergeLibraries`, setlist-screen/model.ts) её не
 * наследуют. `createdAt` — единственное поле даты записи на `LibraryItem`
 * (`recordedAt` есть только у `SetlistSong.versions` с сервера единого
 * сетлиста, setlist.ts, не здесь).
 */
function newestFirst(versions: LibraryItem[]): LibraryItem[] {
  return versions.slice().sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
}

/**
 * Stitch audio groups (grouped by songId, see groupBySong) to words
 * publications into one row per song — joined by SONG ID:
 * `SongTextSummary.localSongId` against the group's `songId` (itself
 * `LibraryItem.songId`, see groupBySong).
 *
 * Wave D: this used to join on the song NAME, compared case-insensitively,
 * because `SongTextSummary` carried no id to join on at all. That broke on
 * exactly the two cases this wave exists to fix: renaming a song after its
 * words were published detached the words from the audio (the row split in
 * two, since the names no longer matched); and two different songs sharing a
 * name made the words attach to whichever group the code happened to look at
 * first, silently gluing them to the wrong song. `local_song_id` has lived on
 * the `song_texts` row since 0018 (wave B) — this wave is only the plumbing
 * that gets it out to this client and used as the join key it always should
 * have been. Name is no longer read at all for matching purposes.
 *
 * Each words publication (by id) attaches to at most ONE audio group —
 * `usedTextIds` below is what guarantees this: once a publication is taken,
 * no later group can take the SAME publication again. Structurally, under an
 * id join, a single publication's `localSongId` can equal at most one
 * group's `songId` (groupBySong keys groups uniquely by songId) — so
 * `usedTextIds` mainly guards the SECOND pass below: without it, a
 * publication already attached to a song row would ALSO be pushed again as
 * its own leftover row (see the "не дублируется" test in song-rows.test.ts).
 * It is kept as the explicit invariant rather than relying on that
 * uniqueness holding by construction.
 *
 * A words publication with no matching audio group (unknown id, or a song
 * that has never had a single take) becomes its own row with empty
 * `versions` — the exact case Wave B shipped desktop-side support for: a
 * song with lyrics/chords but not a single recording yet.
 *
 * Legacy shares published before `songId` existed group under
 * `__share:<shareId>` (see groupBySong) — no `localSongId` is ever that
 * string, so they can never match a words publication and stay separate
 * rows, unchanged from Wave C.
 */
export function buildSongRows(team: TeamLibrary): SongRow[] {
  const groups = groupBySong(team.items);
  const songTexts = team.songTexts ?? [];
  const usedTextIds = new Set<string>();

  const rows: SongRow[] = groups.map((g) => {
    const words: SongTextSummary | null =
      songTexts.find(st => !usedTextIds.has(st.id) && st.localSongId === g.songId) ?? null;
    if (words) usedTextIds.add(words.id);
    return {
      key: g.songId,
      songName: g.songName ?? '',
      songKey: words ? words.songKey : null,
      versions: g.versions,
      words,
      lastOpenedAt: words ? words.lastOpenedAt : null,
    };
  });

  // Leftover words with no matching audio group — their own row.
  for (const st of songTexts) {
    if (usedTextIds.has(st.id)) continue;
    rows.push({
      key: `text:${st.id}`,
      songName: st.songName,
      songKey: st.songKey,
      versions: [],
      words: st,
      lastOpenedAt: st.lastOpenedAt,
    });
  }

  return rows;
}

/** 0 — opened at least once; 1 — has words, never opened; 2 — no words at all. */
function tier(row: SongRow): 0 | 1 | 2 {
  if (row.lastOpenedAt !== null) return 0;
  if (row.words !== null) return 1;
  return 2;
}

/**
 * `recent` — most-recently-opened first; among rows that have words but were
 * never opened, most-recently-published first; rows with no words at all
 * sink to the bottom, alphabetical among themselves. `alpha` — plain name
 * sort. Pure and stable: returns a new array, never mutates `rows`, so the
 * caller can re-sort its original data on every toggle.
 */
export function sortSongRows(rows: SongRow[], sort: SongSort, locale: string): SongRow[] {
  const copy = rows.slice();
  if (sort === 'alpha') {
    copy.sort((a, b) => a.songName.localeCompare(b.songName, locale));
    return copy;
  }
  copy.sort((a, b) => {
    const ta = tier(a);
    const tb = tier(b);
    if (ta !== tb) return ta - tb;
    if (ta === 0) return a.lastOpenedAt! < b.lastOpenedAt! ? 1 : a.lastOpenedAt! > b.lastOpenedAt! ? -1 : 0;
    if (ta === 1) return a.words!.updatedAt < b.words!.updatedAt ? 1 : a.words!.updatedAt > b.words!.updatedAt ? -1 : 0;
    return a.songName.localeCompare(b.songName, locale);
  });
  return copy;
}

/** Substring match on song name, case-insensitive with locale. Blank/whitespace query returns everything. */
export function filterSongRows(rows: SongRow[], query: string, locale: string): SongRow[] {
  const q = query.trim().toLocaleLowerCase(locale);
  if (!q) return rows;
  return rows.filter(r => r.songName.toLocaleLowerCase(locale).includes(q));
}
